/**
 * @tyndo/hooks — typed query/mutation hooks for the predictive governance
 * surface (`/api/v1/predictive/governance/*`; see
 * `Tyndo.Module.Predictive/Endpoints/PredictiveEndpoints.FeatureStoreGovernance.cs`
 * + `.GovernanceDepth.cs`).
 *
 * Two families:
 *   1. Governance workbench rows — feature definitions/sets, deployments,
 *      gates, batch scoring jobs, shadow deployments (the MLOps release flow),
 *      plus their verb actions.
 *   2. Model-governance lifecycle — model card, feature lineage, drift
 *      root-cause, rollback evidence per model (the read-side governance record
 *      previously unreachable from any view).
 *
 * Cache invalidation is tenant-scoped via `invalidateApiQueries`. Predictive-ui
 * views consume these hooks only — no direct fetch.
 */

import { useApiQuery, useApiMutation } from './useApi.ts';
import { useInvalidatePredictive } from './usePredictiveModels.ts';

import {
  type FeatureDefinitionRow,
  type FeatureSetRow,
  type ModelDeploymentRow,
  type ModelGovernanceGateRow,
  type BatchScoringJobRow,
  type ShadowDeploymentRow,
  type ModelCard,
  type ModelCardApprovalStatus,
  type FeatureLineageRow,
  type DriftRootCauseReport,
  type RollbackEvidenceRow,
} from './usePredictiveTypes.ts';

const GOV = '/predictive/governance';

export const PREDICTIVE_FEATURES_ENDPOINT = `${GOV}/feature-definitions`;
export const PREDICTIVE_FEATURE_SETS_ENDPOINT = `${GOV}/feature-sets`;
export const PREDICTIVE_DEPLOYMENTS_ENDPOINT = `${GOV}/deployments`;
export const PREDICTIVE_GATES_ENDPOINT = `${GOV}/gates`;
export const PREDICTIVE_SCORING_ENDPOINT = `${GOV}/scoring-jobs`;
export const PREDICTIVE_SHADOW_ENDPOINT = `${GOV}/shadow-deployments`;

// ── Workbench reads ─────────────────────────────────────────────────────────

/** List feature definitions. */
export function usePredictiveFeatureDefinitions() {
  return useApiQuery<FeatureDefinitionRow[]>(PREDICTIVE_FEATURES_ENDPOINT);
}

/** List feature sets. */
export function usePredictiveFeatureSets() {
  return useApiQuery<FeatureSetRow[]>(PREDICTIVE_FEATURE_SETS_ENDPOINT);
}

/** List model deployments. */
export function usePredictiveDeployments() {
  return useApiQuery<ModelDeploymentRow[]>(PREDICTIVE_DEPLOYMENTS_ENDPOINT);
}

/** List governance gates. */
export function usePredictiveGates() {
  return useApiQuery<ModelGovernanceGateRow[]>(PREDICTIVE_GATES_ENDPOINT);
}

/** List batch scoring jobs. */
export function usePredictiveScoringJobs() {
  return useApiQuery<BatchScoringJobRow[]>(PREDICTIVE_SCORING_ENDPOINT);
}

/** List shadow deployments. */
export function usePredictiveShadowDeployments() {
  return useApiQuery<ShadowDeploymentRow[]>(PREDICTIVE_SHADOW_ENDPOINT);
}

// ── Workbench writes (descriptor-mode action hooks) ─────────────────────────
//
// The workbench issues many distinct writes (feature upsert + status advance,
// deployment create + status advance, gate request + decide, set activate/
// deactivate, shadow promote). Each is a POST to a `{resource}` or
// `{resource}/{id}/{verb}` URL; the body varies per action, so each hook drives
// its family and invalidates the relevant resource prefix(es) on success.

/** Upsert a feature definition (POST /feature-definitions). */
export function useUpsertFeatureDefinition() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<FeatureDefinitionRow>();
  const mutateAsync = async (body: Record<string, unknown>) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: PREDICTIVE_FEATURES_ENDPOINT,
      data: body,
    });
    invalidate(PREDICTIVE_FEATURES_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Advance a feature definition's lifecycle (POST /feature-definitions/{id}/status). */
export function useAdvanceFeatureDefinition() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<FeatureDefinitionRow>();
  const mutateAsync = async (vars: { featureId: string; body: Record<string, unknown> }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_FEATURES_ENDPOINT}/${encodeURIComponent(vars.featureId)}/status`,
      data: vars.body,
    });
    invalidate(PREDICTIVE_FEATURES_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Activate/deactivate a feature set (POST /feature-sets/{id}/{verb}). */
export function useFeatureSetAction() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<FeatureSetRow>();
  const mutateAsync = async (vars: {
    setId: string;
    verb: 'activate' | 'deactivate';
    body?: Record<string, unknown>;
  }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_FEATURE_SETS_ENDPOINT}/${encodeURIComponent(vars.setId)}/${vars.verb}`,
      data: vars.body ?? {},
    });
    invalidate(PREDICTIVE_FEATURE_SETS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Create a model deployment (POST /deployments). */
export function useCreateDeployment() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelDeploymentRow>();
  const mutateAsync = async (body: Record<string, unknown>) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: PREDICTIVE_DEPLOYMENTS_ENDPOINT,
      data: body,
    });
    invalidate(PREDICTIVE_DEPLOYMENTS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Advance a deployment's status incl. typed rollback (POST /deployments/{id}/status). */
export function useAdvanceDeployment() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelDeploymentRow>();
  const mutateAsync = async (vars: { deploymentId: string; body: Record<string, unknown> }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_DEPLOYMENTS_ENDPOINT}/${encodeURIComponent(vars.deploymentId)}/status`,
      data: vars.body,
    });
    invalidate(PREDICTIVE_DEPLOYMENTS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Request a governance gate (POST /gates). */
export function useRequestGate() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelGovernanceGateRow>();
  const mutateAsync = async (body: Record<string, unknown>) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: PREDICTIVE_GATES_ENDPOINT,
      data: body,
    });
    invalidate(PREDICTIVE_GATES_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Record a gate decision (POST /gates/{id}/decide). */
export function useDecideGate() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelGovernanceGateRow>();
  const mutateAsync = async (vars: { gateId: string; body: Record<string, unknown> }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_GATES_ENDPOINT}/${encodeURIComponent(vars.gateId)}/decide`,
      data: vars.body,
    });
    invalidate(PREDICTIVE_GATES_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Promote a shadow deployment candidate (POST /shadow-deployments/{id}/promote). */
export function usePromoteShadowDeployment() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ShadowDeploymentRow>();
  const mutateAsync = async (shadowId: string) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_SHADOW_ENDPOINT}/${encodeURIComponent(shadowId)}/promote`,
      data: {},
    });
    invalidate(PREDICTIVE_SHADOW_ENDPOINT, PREDICTIVE_DEPLOYMENTS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/** Roll back a shadow deployment candidate (POST /shadow-deployments/{id}/rollback). */
export function useRollbackShadowDeployment() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ShadowDeploymentRow>();
  const mutateAsync = async (shadowId: string) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_SHADOW_ENDPOINT}/${encodeURIComponent(shadowId)}/rollback`,
      data: {},
    });
    invalidate(PREDICTIVE_SHADOW_ENDPOINT, PREDICTIVE_DEPLOYMENTS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}

// ── Model-governance lifecycle reads (previously unsurfaced) ─────────────────

/**
 * Operator-curated model card for a model
 * (GET /predictive/governance/model-cards/{modelId}). 404s to a null card when
 * no card has been authored, so `data` is `ModelCard | undefined` and the query
 * is disabled without a model id.
 */
export function useModelCard(modelId: string | null | undefined) {
  return useApiQuery<ModelCard>(
    modelId ? `${GOV}/model-cards/${encodeURIComponent(modelId)}` : '',
    { enabled: Boolean(modelId), retry: false },
  );
}

/**
 * Approval / freshness verdict for a model card
 * (GET /predictive/governance/model-cards/{modelId}/approval). Distinct from
 * the raw card read: this is the computed gate verdict (required-fields
 * complete + approved + not expired) the deploy path enforces, so the approvals
 * console can show why a card is or is not servable without re-deriving it.
 */
export function useModelCardApproval(modelId: string | null | undefined) {
  return useApiQuery<ModelCardApprovalStatus>(
    modelId ? `${GOV}/model-cards/${encodeURIComponent(modelId)}/approval` : '',
    { enabled: Boolean(modelId) },
  );
}

/**
 * Curate (upsert) a model card's governance content
 * (PUT /predictive/governance/model-cards/{modelId}). The body carries only the
 * operator-authored fields; the reviewer / approver identity is never sent from
 * the client — the server stamps LastReviewedAt and resolves the approver from
 * the authenticated principal on approve. Editing an Approved card resets it to
 * Draft server-side, so the caller re-reads the card afterwards.
 */
export interface UpsertModelCardBody {
  intendedUse: string;
  trainingDataDescription: string;
  limitationsKnown: string;
  ethicsReview?: string | null;
  fairnessMetrics?: string | null;
}

export function useUpsertModelCard() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelCard>();
  const mutateAsync = async (vars: { modelId: string; body: UpsertModelCardBody }) => {
    const result = await mutation.mutateAsync({
      method: 'PUT',
      url: `${GOV}/model-cards/${encodeURIComponent(vars.modelId)}`,
      // reviewedByUserId is intentionally omitted: the client cannot assert who
      // reviewed the card — the server records LastReviewedAt itself.
      data: { ...vars.body },
    });
    invalidate(`${GOV}/model-cards/${encodeURIComponent(vars.modelId)}`);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/**
 * Approve a model card (POST /predictive/governance/model-cards/{modelId}/approve).
 * The approver identity — display name and persisted approver-user id / audit
 * actor — is resolved server-side from the authenticated principal; the body
 * carries only an optional approval-expiry. Refused server-side when required
 * fields are incomplete.
 */
export function useApproveModelCard() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelCard>();
  const mutateAsync = async (vars: { modelId: string; expiresAtUtc?: string | null }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${GOV}/model-cards/${encodeURIComponent(vars.modelId)}/approve`,
      data: { expiresAtUtc: vars.expiresAtUtc ?? null },
    });
    invalidate(`${GOV}/model-cards/${encodeURIComponent(vars.modelId)}`);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/**
 * Reject a model card (POST /predictive/governance/model-cards/{modelId}/reject).
 * The rejecter identity is resolved server-side from the authenticated
 * principal; the body carries only the required rejection reason.
 */
export function useRejectModelCard() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<ModelCard>();
  const mutateAsync = async (vars: { modelId: string; reason: string }) => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${GOV}/model-cards/${encodeURIComponent(vars.modelId)}/reject`,
      data: { reason: vars.reason },
    });
    invalidate(`${GOV}/model-cards/${encodeURIComponent(vars.modelId)}`);
    return result;
  };
  return { ...mutation, mutateAsync };
}

/**
 * Append-only feature-lineage rows for a model
 * (GET /predictive/governance/models/{modelId}/feature-lineage).
 */
export function useModelFeatureLineage(modelId: string | null | undefined) {
  return useApiQuery<FeatureLineageRow[]>(
    modelId ? `${GOV}/models/${encodeURIComponent(modelId)}/feature-lineage` : '',
    { enabled: Boolean(modelId) },
  );
}

/**
 * Heuristic drift root-cause report for a model over a window
 * (GET /predictive/governance/models/{modelId}/drift-root-cause?windowDays=N).
 */
export function useModelDriftRootCause(
  modelId: string | null | undefined,
  windowDays?: number,
) {
  // Direct template-literal URL (enabled-guarded) — the query keeps the path
  // segment `/drift-root-cause` literal; 30 days mirrors the server default.
  return useApiQuery<DriftRootCauseReport>(
    `${GOV}/models/${encodeURIComponent(modelId ?? '')}/drift-root-cause?windowDays=${windowDays ?? 30}`,
    { enabled: Boolean(modelId) },
  );
}

/**
 * Append-only rollback evidence records for a model
 * (GET /predictive/governance/rollback-evidence/{modelId}).
 */
export function useModelRollbackEvidence(modelId: string | null | undefined) {
  return useApiQuery<RollbackEvidenceRow[]>(
    modelId ? `${GOV}/rollback-evidence/${encodeURIComponent(modelId)}` : '',
    { enabled: Boolean(modelId) },
  );
}
